import React from 'react';
import {
  showToast,
  mountForm,
  unmountForm,
  SystemSpaceForm,
} from '@kineticdata/react';
import {
  generateFormButtons,
  FormError,
  Icon,
  TableField,
} from '@kineticdata/ui';
import { SpaceHelp } from './SpaceHelp';
import { NavListItemLink } from '../common/NavListItemLink';
import { SpaceEditUsersList } from './users/SpaceEditUsersList';
import { regRoute } from '../../router';
import { regSaga } from '../../saga';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { TimeAgoAndUser } from '../common/TimeAgoAndUser';
import { StatusBadge } from '../common/StatusBadge';
import { openModal } from '../common/ConfirmationModal';
import { action } from '../../store';
import { Link, useParams } from 'react-router-dom';
import {
  fetchSystemDefaultSQLDbAdapter,
  fetchTenant,
} from '@kineticdata/react/src/apis/system';
import { spaceDeleteConfirmOptions } from './index';
import { SpaceSecurityTab } from './SpaceSecurityTab';
import { SpaceEditIntegrator } from './SpaceEditIntegrator';
import { SpaceEditTask } from './SpaceEditTask';

const tenantEditKey = spaceSlug => `${spaceSlug}-tenant-edit`;

const handleSave = () => () => {
  showToast({ content: `The space was updated successfully.` }, 'success');
};

regRoute('TENANT_EDIT_PAGE', '/spaces/edit/:spaceSlug/*');

regSaga(
  takeEvery('ENTER_TENANT_EDIT_PAGE', function* ({ payload }) {
    // Mount the form.
    const { spaceSlug } = payload.params;
    yield call(mountForm, tenantEditKey(spaceSlug));

    // Fetch the data needed for the db deletion checkbox.
    const [{ tenant }, { adapter }] = yield all([
      call(fetchTenant, { slug: spaceSlug, include: 'details' }),
      call(fetchSystemDefaultSQLDbAdapter),
    ]);

    // Allow deletion if either database is postgres
    let canDeleteTaskDb = tenant?.task?.databaseAdapter?.type === 'postgres';
    let canDeleteIntegratorDb =
      tenant?.integrator?.databaseAdapter?.type === 'postgres';
    // Default delete checkbox to true if either can be deleted
    let willDeleteDb = !!canDeleteTaskDb || !!canDeleteIntegratorDb;

    // If task db can be deleted but doesn't match the default adapter, uncheck
    // the will delete checkbox
    if (canDeleteTaskDb && !!adapter && willDeleteDb) {
      if (
        tenant.task.databaseAdapter.properties.find(p => p.name === 'host')
          ?.value !== adapter.properties.find(p => p.name === 'host')?.value ||
        tenant.task.databaseAdapter.properties.find(p => p.name === 'port')
          ?.value !== adapter.properties.find(p => p.name === 'port')?.value
      ) {
        willDeleteDb = false;
      }
    }

    // If integrator db can be deleted but doesn't match the default adapter,
    // uncheck the will delete checkbox
    if (canDeleteIntegratorDb && !!adapter && willDeleteDb) {
      if (
        tenant.integrator.databaseAdapter.properties.find(
          p => p.name === 'host',
        )?.value !== adapter.properties.find(p => p.name === 'host')?.value ||
        tenant.integrator.databaseAdapter.properties.find(
          p => p.name === 'port',
        )?.value !== adapter.properties.find(p => p.name === 'port')?.value
      ) {
        willDeleteDb = false;
      }
    }

    yield put({
      type: 'SET_DELETE_TENANT_DB',
      payload: {
        canDeleteTaskDb,
        canDeleteIntegratorDb,
        willDeleteDb,
      },
    });

    yield put({
      type: 'SET_TENANT_FEATURES',
      payload: {
        taskEnabled: tenant?.features?.task === 'true',
        integratorEnabled: tenant?.features?.integrator === 'true',
      },
    });
  }),
);

regSaga(
  takeEvery('LEAVE_TENANT_EDIT_PAGE', function* ({ payload }) {
    yield call(unmountForm, tenantEditKey(payload));
    yield put({ type: 'RESET_DELETE_TENANT_DB' });
    yield put({ type: 'RESET_TENANT_FEATURES' });
  }),
);

export const spaceConsoleUrl = spaceSlug => {
  const { protocol, host } = window.location;
  return `${protocol}//${spaceSlug}.${host}/app/`;
};

const FormLayout = ({ fields, error, buttons }) => (
  <form>
    <div className="row">
      <div className="col-6">{fields.get('name')}</div>
      <div className="col-6">{fields.get('slug')}</div>
    </div>
    {fields.get('sharedBundleBase')}
    {fields.get('bundlePath')}
    {fields.get('allowedIpsEnabled')}
    {fields.get('allowedIps')}
    {error}
    <div className="form-buttons">{buttons}</div>
  </form>
);

export const SpaceEdit = () => {
  const { spaceSlug, tab } = useParams();

  const handleDelete = () =>
    openModal({
      ok: action('DELETE_SPACE', spaceSlug),
      ...spaceDeleteConfirmOptions({ spaceSlug }),
    });

  return (
    <SystemSpaceForm
      formKey={tenantEditKey(spaceSlug)}
      slug={spaceSlug}
      fieldSet={[
        'name',
        'slug',
        'sharedBundleBase',
        'bundlePath',
        'allowedIpsEnabled',
        'allowedIps',
      ]}
      components={{
        FormError,
        FormButtons: generateFormButtons({
          handleDelete,
          submitLabel: spaceSlug ? 'Update Space' : 'Create Space',
          includeReset: true,
        }),
        FormLayout,
      }}
      alterFields={{
        allowedIps: { component: TableField },
        allowedIpsEnabled: { renderAttributes: { switch: true } },
      }}
      onSave={handleSave}
    >
      {({ form, initialized, bindings: { space } }) =>
        initialized && (
          <div className="app-content__main">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/spaces">Spaces</Link>
                </li>
                <li className="breadcrumb-item active">{space.get('name')}</li>
              </ol>
            </nav>
            <div className="heading">
              <h1>
                {space.get('name')}
                <SpaceHelp />
              </h1>
              <div className="btn-set">
                <a
                  className="btn btn-primary"
                  href={spaceConsoleUrl(spaceSlug)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>Space Console</span>
                  <Icon name="arrow-up-right" />
                </a>
              </div>
            </div>
            <dl className="metadata">
              <div>
                <dt>Created:</dt>
                <dd>
                  <TimeAgoAndUser
                    time={space.get('createdAt')}
                    username={space.get('createdBy')}
                  />
                </dd>
              </div>
              <div>
                <dt>Updated:</dt>
                <dd>
                  <TimeAgoAndUser
                    time={space.get('updatedAt')}
                    username={space.get('updatedBy')}
                  />
                </dd>
              </div>
              <div>
                <dt>Status:</dt>
                <dd>
                  <StatusBadge sm value={space.get('status')} />
                </dd>
              </div>
              {space.get('statusMessage') && (
                <div>
                  <dt>Message:</dt>
                  <dd>{space.get('statusMessage')}</dd>
                </div>
              )}
              <div className="d-flex justify-content-end ms-auto">
                <dt>
                  <Link
                    to={`/spaces/migrate/${spaceSlug}`}
                    className="migrate-space-link"
                  >
                    Migrate Space
                  </Link>
                </dt>
              </div>
            </dl>

            <div className="columns">
              <div className="col-nav">
                <ul className="nav flex-column">
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/general`}>
                    General
                  </NavListItemLink>
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/users`}>
                    Users
                  </NavListItemLink>
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/integrator`}>
                    Integrator
                  </NavListItemLink>
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/security`}>
                    Security
                  </NavListItemLink>
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/task`}>
                    Task
                  </NavListItemLink>
                </ul>
              </div>
              <div className="col">
                {tab === 'general' ? (
                  <div className="content-container">{form}</div>
                ) : tab === 'users' ? (
                  <SpaceEditUsersList spaceSlug={spaceSlug} />
                ) : tab === 'integrator' ? (
                  <SpaceEditIntegrator
                    spaceSlug={spaceSlug}
                    handleDelete={handleDelete}
                  />
                ) : tab === 'security' ? (
                  <SpaceSecurityTab spaceSlug={spaceSlug} />
                ) : tab === 'task' ? (
                  <SpaceEditTask
                    spaceSlug={spaceSlug}
                    handleDelete={handleDelete}
                  />
                ) : null}
              </div>
            </div>
          </div>
        )
      }
    </SystemSpaceForm>
  );
};
